import { useState, useCallback, useEffect, useId, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Code2, Plus, X } from "lucide-react";
import {
  ajv,
  CustomFieldsSchemaSchema,
  getCustomFieldOptions,
  getRequiredCustomFieldNames,
  isBlankCustomFieldValue,
  type CustomFieldProperty,
  type CustomFieldsSchema,
} from "@forgepoint/shared";
import {
  CUSTOM_FIELD_TYPE_ICONS,
  CUSTOM_FIELD_TYPE_LABELS,
  CUSTOM_FIELD_TYPES,
  deriveCustomFieldType,
  isCustomFieldType,
  type CustomFieldType,
} from "@/lib/customFieldTypes";
import { SelectOptionsEditor } from "./SelectOptionsEditor";
import { CustomFieldDefaultValueInput } from "./CustomFieldDefaultValueInput";

/** A single row in the editor representing one custom field definition. */
interface FieldRow {
  name: string;
  fieldType: CustomFieldType;
  options?: string[];
  description?: string;
  required: boolean;
  readOnly: boolean;
  defaultValue?: string | number | string[];
}

/** Props for the CustomFieldsSchemaEditor component. */
interface CustomFieldsSchemaEditorProps {
  schema: CustomFieldsSchema | null;
  onChange: (schema: CustomFieldsSchema | null) => void;
  disabled?: boolean;
}

/**
 * UI editor that translates between a flat JSON Schema and user-friendly field rows.
 * Allows adding/removing custom field definitions with name and type selection.
 */
export function CustomFieldsSchemaEditor({
  schema,
  onChange,
  disabled = false,
}: CustomFieldsSchemaEditorProps) {
  const [rows, setRows] = useState<FieldRow[]>(() => schemaToRows(schema));
  const [manualEditorOpen, setManualEditorOpen] = useState(false);
  const [manualJson, setManualJson] = useState("");
  const [manualError, setManualError] = useState<string | null>(null);
  const isInternalChange = useRef(false);
  const editorId = useId();

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    setRows(schemaToRows(schema));
  }, [schema]);

  const emitChange = useCallback(
    (updated: FieldRow[]) => {
      isInternalChange.current = true;
      if (updated.length === 0) {
        onChange(null);
        return;
      }
      onChange(rowsToSchema(updated));
    },
    [onChange],
  );

  const handleAddField = useCallback(() => {
    const newRow: FieldRow = {
      name: "",
      fieldType: "string",
      required: false,
      readOnly: false,
    };
    const updated = [...rows, newRow];
    setRows(updated);
  }, [rows]);

  const handleOpenManualEditor = useCallback(() => {
    setManualJson(formatSchemaForManualEdit(schema));
    setManualError(null);
    setManualEditorOpen(true);
  }, [schema]);

  const handleApplyManualJson = useCallback(() => {
    const parsed = parseManualSchema(manualJson);
    if (!parsed.ok) {
      setManualError(parsed.error);
      return;
    }

    isInternalChange.current = true;
    setRows(schemaToRows(parsed.schema));
    onChange(parsed.schema);
    setManualEditorOpen(false);
    setManualError(null);
  }, [manualJson, onChange]);

  const handleRemoveField = useCallback(
    (index: number) => {
      const updated = rows.filter((_, i) => i !== index);
      setRows(updated);
      emitChange(updated);
    },
    [rows, emitChange],
  );

  const handleNameChange = useCallback(
    (index: number, name: string) => {
      const updated = rows.map((row, i) =>
        i === index ? { ...row, name } : row,
      );
      setRows(updated);
      if (name.length > 0 && !hasDuplicateNames(updated)) {
        emitChange(updated);
      }
    },
    [rows, emitChange],
  );

  const handleTypeChange = useCallback(
    (index: number, fieldType: FieldRow["fieldType"]) => {
      const updated = rows.map((row, i) => {
        if (i !== index) {
          return row;
        }
        if (fieldType === "select" || fieldType === "multiSelect") {
          return withCompatibleDefault({
            ...row,
            fieldType,
            options: row.options?.length ? row.options : ["", ""],
          });
        }
        const { options: _removed, ...rest } = row;
        return withCompatibleDefault({ ...rest, fieldType });
      });
      setRows(updated);
      if (updated[index].name.length > 0) {
        emitChange(updated);
      }
    },
    [rows, emitChange],
  );

  const handleOptionsChange = useCallback(
    (index: number, options: string[]) => {
      const nonEmpty = options.filter((o) => o.length > 0);
      const hasDuplicates =
        new Set(nonEmpty.map((o) => o.toLowerCase())).size !== nonEmpty.length;
      const updated = rows.map((row, i) =>
        i === index ? withCompatibleDefault({ ...row, options }) : row,
      );
      setRows(updated);
      if (!hasDuplicates && updated[index].name.length > 0) {
        emitChange(updated);
      }
    },
    [rows, emitChange],
  );

  const handleDescriptionChange = useCallback(
    (index: number, description: string) => {
      const updated = rows.map((row, i) =>
        i === index ? { ...row, description: description || undefined } : row,
      );
      setRows(updated);
      if (updated[index].name.length > 0) {
        emitChange(updated);
      }
    },
    [rows, emitChange],
  );

  const handleRequiredChange = useCallback(
    (index: number, required: boolean) => {
      const updated = rows.map((row, i) =>
        i === index ? { ...row, required } : row,
      );
      setRows(updated);
      if (updated[index].name.length > 0) {
        emitChange(updated);
      }
    },
    [rows, emitChange],
  );

  const handleReadOnlyChange = useCallback(
    (index: number, readOnly: boolean) => {
      const updated = rows.map((row, i) =>
        i === index ? { ...row, readOnly } : row,
      );
      setRows(updated);
      if (updated[index].name.length > 0) {
        emitChange(updated);
      }
    },
    [rows, emitChange],
  );

  const handleDefaultValueChange = useCallback(
    (index: number, defaultValue: string | number | string[] | undefined) => {
      const updated = rows.map((row, i) =>
        i === index ? { ...row, defaultValue } : row,
      );
      setRows(updated);
      if (updated[index].name.length > 0) {
        emitChange(updated);
      }
    },
    [rows, emitChange],
  );

  const nonEmptyNames = rows.map((r) => r.name).filter((n) => n.length > 0);
  const lowerNames = nonEmptyNames.map((n) => n.toLowerCase());
  const duplicateNameSet = new Set(
    lowerNames.filter((v, i) => lowerNames.indexOf(v) !== i),
  );

  return (
    <div className="space-y-3">
      {rows.length === 0 && (
        <div className="rounded-lg border border-dashed py-6 text-center">
          <p className="text-sm text-muted-foreground">
            No custom fields defined yet
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            Add fields to capture additional data
          </p>
        </div>
      )}
      {rows.length > 0 && (
        <div className="rounded-lg border divide-y overflow-hidden">
          {rows.map((row, index) => (
            <div
              key={index}
              className="bg-background hover:bg-muted/30 transition-colors group"
            >
              <div className="flex items-center gap-2.5 px-3 py-2.5">
                <Input
                  placeholder="Field name"
                  value={row.name}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                  disabled={disabled}
                  className={`flex-1 h-8 text-sm ${row.name.length > 0 && duplicateNameSet.has(row.name.toLowerCase()) ? "border-destructive focus-visible:ring-destructive/30" : ""}`}
                />
                <Select
                  value={row.fieldType}
                  onValueChange={(value) => {
                    if (isCustomFieldType(value)) {
                      handleTypeChange(index, value);
                    }
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger className="w-[130px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOM_FIELD_TYPES.map((type) => {
                      const Icon = CUSTOM_FIELD_TYPE_ICONS[type];
                      return (
                        <SelectItem key={type} value={type}>
                          <span className="flex items-center gap-1.5">
                            <Icon className="h-3 w-3 text-muted-foreground" />
                            {CUSTOM_FIELD_TYPE_LABELS[type]}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveField(index)}
                  disabled={disabled}
                  className="shrink-0 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              {row.name.length > 0 && (
                <div className="px-3 pb-2.5 -mt-1">
                  <Input
                    placeholder="Add a description..."
                    value={row.description ?? ""}
                    onChange={(e) =>
                      handleDescriptionChange(index, e.target.value)
                    }
                    disabled={disabled}
                    className="h-7 text-xs text-muted-foreground border-none shadow-none bg-transparent px-0 placeholder:text-muted-foreground/40 focus-visible:ring-0"
                  />
                </div>
              )}
              <Collapsible
                open={
                  row.fieldType === "select" || row.fieldType === "multiSelect"
                }
              >
                <CollapsibleContent className="animate-collapsible-down data-[state=closed]:animate-collapsible-up">
                  <SelectOptionsEditor
                    options={row.options ?? []}
                    onChange={(options) => handleOptionsChange(index, options)}
                    disabled={disabled}
                  />
                </CollapsibleContent>
              </Collapsible>
              {row.name.length > 0 && (
                /* Wraps rather than squeezes, so a narrow settings panel stacks
                   the toggles under the default input. */
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 pb-2.5">
                  {row.fieldType !== "user" &&
                    row.fieldType !== "multiUser" &&
                    row.fieldType !== "attachments" && (
                      <div className="flex min-w-0 grow basis-[240px] items-center gap-2">
                        <span className="shrink-0 text-xs text-muted-foreground">
                          Default
                        </span>
                        <div className="min-w-0 flex-1">
                          <CustomFieldDefaultValueInput
                            fieldType={row.fieldType}
                            options={validOptionsOf(row)}
                            value={row.defaultValue}
                            onChange={(value) =>
                              handleDefaultValueChange(index, value)
                            }
                            disabled={disabled}
                          />
                        </div>
                      </div>
                    )}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`${editorId}-required-${index}`}
                        checked={row.required}
                        onCheckedChange={(checked) =>
                          handleRequiredChange(index, checked)
                        }
                        disabled={disabled}
                      />
                      <Label
                        htmlFor={`${editorId}-required-${index}`}
                        className="text-xs font-normal text-muted-foreground"
                      >
                        Required
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`${editorId}-readonly-${index}`}
                        checked={row.readOnly}
                        onCheckedChange={(checked) =>
                          handleReadOnlyChange(index, checked)
                        }
                        disabled={disabled}
                      />
                      <Label
                        htmlFor={`${editorId}-readonly-${index}`}
                        className="text-xs font-normal text-muted-foreground"
                      >
                        Read-only for users
                      </Label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {duplicateNameSet.size > 0 && (
        <p className="text-xs text-destructive">
          Duplicate field names are not allowed
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddField}
          disabled={disabled}
          className="h-8 text-xs"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add Field
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpenManualEditor}
          disabled={disabled}
          className="h-8 text-xs"
        >
          <Code2 className="mr-1.5 h-3.5 w-3.5" />
          Update manually
        </Button>
      </div>

      <Sheet open={manualEditorOpen} onOpenChange={setManualEditorOpen}>
        <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
          <SheetHeader className="border-b px-6 py-4">
            <SheetTitle>Update custom fields manually</SheetTitle>
            <SheetDescription>
              Paste a custom fields JSON schema. Use null to remove all custom
              fields.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <Textarea
              value={manualJson}
              onChange={(event) => {
                setManualJson(event.target.value);
                setManualError(null);
              }}
              autosize={false}
              rows={18}
              spellCheck={false}
              className="min-h-[360px] resize-none font-mono text-xs"
            />
            {manualError && (
              <p className="mt-2 text-xs text-destructive">{manualError}</p>
            )}
          </div>
          <SheetFooter className="border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setManualEditorOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleApplyManualJson}>
              Apply
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/**
 * Checks whether any non-empty field names in the given rows are duplicates (case-insensitive).
 * @param fieldRows - The rows to check
 * @returns true if duplicate names exist
 */
function hasDuplicateNames(fieldRows: FieldRow[]): boolean {
  const names = fieldRows
    .map((r) => r.name)
    .filter((n) => n.length > 0)
    .map((n) => n.toLowerCase());
  return new Set(names).size !== names.length;
}

type ManualSchemaParseResult =
  | { ok: true; schema: CustomFieldsSchema | null }
  | { ok: false; error: string };

function formatSchemaForManualEdit(schema: CustomFieldsSchema | null): string {
  return JSON.stringify(schema, null, 2);
}

function parseManualSchema(value: string): ManualSchemaParseResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (error) {
    return {
      ok: false,
      error:
        error instanceof Error ? error.message : "Invalid custom fields JSON",
    };
  }

  if (parsed === null) {
    return { ok: true, schema: null };
  }

  const result = CustomFieldsSchemaSchema.safeParse(parsed);
  if (!result.success) {
    return {
      ok: false,
      error: result.error.issues
        .map((issue) => {
          const path = issue.path.length > 0 ? issue.path.join(".") : "schema";
          return `${path}: ${issue.message}`;
        })
        .join("; "),
    };
  }

  return { ok: true, schema: result.data };
}

/** Convert a JSON Schema to an array of editor rows, respecting propertyOrder if present. */
function schemaToRows(schema: CustomFieldsSchema | null): FieldRow[] {
  if (!schema?.properties) {
    return [];
  }
  const orderedKeys = schema.propertyOrder ?? Object.keys(schema.properties);
  const propertyNames = new Set(Object.keys(schema.properties));
  const requiredNames = new Set(getRequiredCustomFieldNames(schema));
  const result: FieldRow[] = [];

  for (const name of orderedKeys) {
    if (!propertyNames.has(name)) {
      continue;
    }
    result.push(toFieldRow(name, schema.properties[name], requiredNames));
  }
  for (const name of propertyNames) {
    if (!orderedKeys.includes(name)) {
      result.push(toFieldRow(name, schema.properties[name], requiredNames));
    }
  }

  return result;
}

/**
 * Build a single editor row from a schema property.
 * @param name - The custom field name
 * @param prop - The property definition from the schema
 * @param requiredNames - The names the schema marks as required
 * @returns The editor row for the property
 */
function toFieldRow(
  name: string,
  prop: CustomFieldProperty,
  requiredNames: Set<string>,
): FieldRow {
  const options = getCustomFieldOptions(prop) ?? undefined;
  return {
    name,
    fieldType: deriveCustomFieldType(prop),
    required: requiredNames.has(name),
    readOnly: prop.readOnly === true,
    ...(options ? { options } : {}),
    ...(prop.description ? { description: prop.description } : {}),
    ...(prop.default !== undefined ? { defaultValue: prop.default } : {}),
  };
}

/** Convert editor rows back to a JSON Schema. Skips rows with empty names. */
function rowsToSchema(rows: FieldRow[]): CustomFieldsSchema {
  const properties: CustomFieldsSchema["properties"] = {};
  const propertyOrder: string[] = [];
  const required: string[] = [];

  const emit = (row: FieldRow, prop: CustomFieldProperty): void => {
    properties[row.name] = prop;
    propertyOrder.push(row.name);
    if (row.required) {
      required.push(row.name);
    }
  };

  for (const unsanitized of rows) {
    const row = withCompatibleDefault(unsanitized);
    if (row.name.trim().length === 0) {
      continue;
    }
    const prop = rowToProperty(row);
    if (prop) {
      emit(row, prop);
    }
  }

  return {
    type: "object",
    properties,
    additionalProperties: false,
    propertyOrder,
    ...(required.length > 0 ? { required } : {}),
  };
}

/**
 * Build the JSON Schema property a row describes.
 * @param row - The editor row
 * @returns The property, or null when a select row has no options yet
 */
function rowToProperty(row: FieldRow): CustomFieldProperty | null {
  const shared = {
    ...(row.description ? { description: row.description } : {}),
    ...(isBlankCustomFieldValue(row.defaultValue)
      ? {}
      : { default: row.defaultValue }),
    ...(row.readOnly ? { readOnly: true } : {}),
  };
  if (row.fieldType === "select") {
    const validOptions = validOptionsOf(row);
    if (validOptions.length === 0) {
      return null;
    }
    return {
      type: ["string", "null"],
      format: "select",
      enum: [null, ...validOptions],
      ...shared,
    };
  }
  if (row.fieldType === "multiSelect") {
    const validOptions = validOptionsOf(row);
    if (validOptions.length === 0) {
      return null;
    }
    return {
      type: ["array", "null"],
      items: { type: "string", enum: validOptions },
      ...shared,
    };
  }
  if (row.fieldType === "multiUser") {
    return {
      type: ["array", "null"],
      items: { type: "string" },
      format: "fpUser",
      ...shared,
    };
  }
  if (row.fieldType === "attachments") {
    return {
      type: ["array", "null"],
      items: { type: "string" },
      format: "fpAsset",
      ...shared,
    };
  }
  const schemaType: "string" | "number" =
    row.fieldType === "number" || row.fieldType === "percentage"
      ? "number"
      : "string";
  const format =
    row.fieldType === "date"
      ? "date"
      : row.fieldType === "user"
        ? "fpUser"
        : row.fieldType === "percentage"
          ? "percentage"
          : undefined;
  return {
    type: [schemaType, "null"],
    ...(format ? { format } : {}),
    ...shared,
  };
}

/**
 * Drop a row's default value when it no longer validates against the property the
 * row emits, so the editor never produces a schema the shared validator would reject.
 * User and attachment fields never carry a default, and a multi-select default is first narrowed
 * to the options that still exist.
 * @param row - The editor row to sanitize
 * @returns The row with an incompatible default removed or narrowed
 */
function withCompatibleDefault(row: FieldRow): FieldRow {
  const { defaultValue, ...rest } = row;
  if (defaultValue === undefined) {
    return row;
  }
  if (
    row.fieldType === "user" ||
    row.fieldType === "multiUser" ||
    row.fieldType === "attachments"
  ) {
    return rest;
  }
  const candidate =
    row.fieldType === "multiSelect" && Array.isArray(defaultValue)
      ? defaultValue.filter((option) => validOptionsOf(row).includes(option))
      : defaultValue;
  const property = rowToProperty(rest);
  if (!property || isBlankCustomFieldValue(candidate)) {
    return rest;
  }
  const validate = ajv.compile(property);
  ajv.removeSchema(property);
  return validate(candidate) ? { ...rest, defaultValue: candidate } : rest;
}

/**
 * Resolve a row's option values, ignoring blank entries still being typed.
 * @param row - The editor row
 * @returns The non-blank option values
 */
function validOptionsOf(row: FieldRow): string[] {
  return (row.options ?? []).filter((option) => option.trim().length > 0);
}
